import { useConfig } from "@/context/ConfigContext";
import { useDevice } from "@/context/DeviceContext";
import { useEffect, useMemo, useState } from "react";

/**
 * Hook untuk mengambil shipping rates
 * @param {Object} options - Options untuk fetch
 * @param {Array} options.items - List item untuk shipping rates
 * @param {string} options.city_code - City code tujuan (opsional)
 * @param {string} options.destination_district_code - District code tujuan
 * @param {string|null} options.destination_sub_district_code - Subdistrict code tujuan
 * @param {number} options.insurance - Insurance flag (0/1)
 * @param {boolean} options.enabled - Enable/disable fetch (default: true)
 */
export function useShippingRates(options = {}) {
  const {
    items = [],
    city_code,
    destination_district_code,
    destination_sub_district_code,
    insurance = 1,
    enabled = true,
  } = options;
  const { config, configData } = useConfig();
  const { deviceId } = useDevice();
  const [shippingRates, setShippingRates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Hindari refetch berulang karena referensi array `items` berubah-ubah.
  // Kunci diambil dari isi items.
  const itemsKey = useMemo(() => {
    if (!Array.isArray(items) || items.length === 0) return "";
    try {
      // JSON stringify cukup stabil karena struktur items sederhana.
      return JSON.stringify(items);
    } catch {
      return "";
    }
  }, [items]);

  useEffect(() => {
    if (
      !enabled ||
      !configData?.team_id ||
      !config?.api?.baseUrl ||
      !items.length ||
      !destination_district_code
    ) {
      return;
    }

    const fetchShippingRates = async () => {
      try {
        setLoading(true);
        setError(null);

        const teamId = configData.team_id;

        const proxyUrl = `/api/checkout?team_id=${teamId}&action=shipping-rates`;

        console.log("[useShippingRates] Fetching shipping rates:", {
          proxyUrl,
          teamId,
          items,
          destination_district_code,
          destination_sub_district_code,
          insurance,
        });

        const response = await fetch(proxyUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(deviceId ? { "X-DEVICE-ID": deviceId } : {}),
          },
          body: JSON.stringify({
            items,
            destination_district_code,
            destination_sub_district_code:
              destination_sub_district_code || null,
            couriers: [],
          }),
          cache: "no-cache",
        });

        if (!response.ok) {
          let errorData;
          let errorText;

          try {
            errorText = await response.text();
            errorData = JSON.parse(errorText);
          } catch (parseError) {
            errorData = {
              message: errorText || response.statusText,
              status: response.status,
            };
          }

          console.error("[useShippingRates] API Error:", errorData);
          throw new Error(
            errorData.message ||
              errorData.error ||
              errorText ||
              response.statusText
          );
        }

        const data = await response.json();
        setShippingRates(data.data || data);
      } catch (err) {
        console.error("[useShippingRates] Error fetching shipping rates:", {
          message: err.message,
          stack: err.stack,
          teamId: configData?.team_id,
          itemsCount: items.length,
        });
        setError(err.message || "Gagal mengambil shipping rates");
      } finally {
        setLoading(false);
      }
    };

    fetchShippingRates();
  }, [
    configData?.team_id,
    config?.api?.baseUrl,
    itemsKey,
    destination_district_code,
    destination_sub_district_code,
    insurance,
    enabled,
  ]);

  return {
    shippingRates,
    loading,
    error,
  };
}
